import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Shield } from 'lucide-react';
import { useDarkMode } from '../../contexts/DarkModeContext';

const SuperAdminRoute = ({ children }) => {
  const { currentUser, isSuperAdmin, loading } = useAuth();
  const { isDarkMode } = useDarkMode();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
      }`}>
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Verifying permissions...
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated at all
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but not super admin
  if (!isSuperAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
      }`}>
        <div className={`max-w-md w-full mx-4 p-8 rounded-[24px] border text-center ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-center mb-4">
            <div className={`p-4 rounded-full ${
              isDarkMode ? 'bg-red-500/20' : 'bg-red-100'
            }`}>
              <Shield className={`w-8 h-8 ${
                isDarkMode ? 'text-red-400' : 'text-red-600'
              }`} />
            </div>
          </div>

          <h1 className={`text-2xl font-bold mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Access Denied
          </h1>

          <p className={`mb-6 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            You don't have permission to access the admin dashboard.
          </p>

          <div className={`p-4 rounded-xl border ${
            isDarkMode
              ? 'bg-yellow-900/20 border-yellow-500/30'
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
              <p className={`text-sm text-left ${
                isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
              }`}>
                If you believe this is an error, please contact the system administrator.
              </p>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/dashboard'}
            className={`mt-6 w-full px-4 py-2.5 rounded-xl transition-all duration-200 ${
              isDarkMode
                ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default SuperAdminRoute;
